import { PermissionEnum } from '@Application/Security/Roles';
import useDocumentTemplatesTab from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Hooks/useDocumentTemplatesTab';
import { openDocumentTemplateUploadSidebar } from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTemplateUploadSidebarSignal';
import { openDocumentTypeSidebarForCreation } from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTypeSidebarSignal';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

/**
 * The page header's single action button, swapped by the active tab, the caller's own permissions,
 * and whether the screen currently shows its tabs at all. "Ajouter un type de document" only when all
 * three hold at once: the "Affectation par type" tab is active, the caller holds
 * `DOCUMENT_TYPE_WRITE`, and at least one gabarit is loaded for the current organization — the
 * empty-deposit state keeps its own "Déposer un gabarit" action instead, never this one. Every other
 * case falls back to the unchanged "Déposer un gabarit" (`DOCUMENT_TEMPLATE_WRITE`) — in particular, a
 * caller without `DOCUMENT_TYPE_WRITE` sees only "Déposer un gabarit", whatever tab is active. Plain
 * conditional JSX gated on `useHasPermission`, not a `WithPermission` wrapper,
 * since only one of two single-prop buttons is ever rendered — same pattern as `OrganizationsPage`.
 */
const DocumentTemplatesHeaderActions: React.FC = () => {
	const { activeTab } = useDocumentTemplatesTab();
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;
	const hasDocumentTypeWritePermission = useHasPermission(PermissionEnum.DOCUMENT_TYPE_WRITE);
	const hasDocumentTemplateWritePermission = useHasPermission(PermissionEnum.DOCUMENT_TEMPLATE_WRITE);

	const hasLoadedTemplates = useSelector((state) => {
		const templates = state.documentTemplatesStore.templates;
		return templates !== null && templates.organizationId === organizationId && templates.list.length > 0;
	});

	const handleCreateTypeClick = React.useCallback(() => {
		openDocumentTypeSidebarForCreation();
	}, []);

	const handleUploadClick = React.useCallback(() => {
		openDocumentTemplateUploadSidebar({ mode: 'create' });
	}, []);

	if (activeTab === 'assignments' && hasLoadedTemplates && hasDocumentTypeWritePermission) {
		return (
			<Button
				type="button"
				label="Ajouter un type de document"
				icon={<Plus size={16} />}
				onClick={handleCreateTypeClick}
			/>
		);
	}

	if (!hasDocumentTemplateWritePermission) {
		return null;
	}

	return (
		<Button
			type="button"
			label="Déposer un gabarit"
			icon={<Plus size={16} />}
			onClick={handleUploadClick}
		/>
	);
};

export default DocumentTemplatesHeaderActions;
